import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StockTableLayout, LockIcon } from '../../components/StockTable';
import { TickerDisplayData } from '../../types/ticker';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useAddTicker } from '../../hooks/useAddTicker';
import { useModals } from '../../hooks/useModals';
import { useListPreferences } from '../../hooks/useListPreferences';
import TabBar from '../../components/Common/TabBar';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import ShareListModal from '../../components/ShareListModal';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import {
  cacheCurrentListName,
  getCachedCurrentListName,
  getCachedCurrentListId,
  clearListCache,
} from '../../utils/listCache';
import {
  PROS_CONS_STATISTICS,
  prosConsLineArrays,
  ProsConsNumberedList,
  createEtfPlaceholderProsConsRow,
} from '../../utils/prosConsTabUtils';
import { isEtfOrEtvMarketType } from '../../utils/insiderTransactionsTabUtils';

type ProsConsRow = {
  tickerSymbol: string;
  tickerBasicsId: number;
  year: number | null;
  month: number | null;
  prosCons: {
    pro_1: string | null;
    pro_2: string | null;
    pro_3: string | null;
    pro_4: string | null;
    pro_5: string | null;
    con_1: string | null;
    con_2: string | null;
    con_3: string | null;
    con_4: string | null;
    con_5: string | null;
  } | null;
};

const ListProsCons: React.FC = () => {
  useEffect(() => {
    document.title = 'Stock Table - Pros & Cons';
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;

  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
  } | null>(null);
  const needsUserListsReload = useRef(false);
  const lastFetchedTickerListRef = useRef<string>('');
  const prevTickerListRef = useRef<string[]>([]);

  const [isEditingTickers, setIsEditingTickers] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const effectiveHasPro = useMemo(() => {
    if (isPublicList && publicListData) return publicListData.ownerHasProSubscription;
    return Boolean(isAuthenticated && user?.hasProSubscription);
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  const effectiveHasProRef = useRef(false);
  useEffect(() => {
    effectiveHasProRef.current = effectiveHasPro;
  }, [effectiveHasPro]);

  // All indicators are locked (Pro-only)
  const lockedStatistics = useMemo(() => {
    const allKeys = PROS_CONS_STATISTICS.map(s => s.key);
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : allKeys;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? allKeys : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);

  const [tickerList, setTickerList] = useState<string[]>(() => {
    if (isPublicList) return [];
    if (isAuthenticated) return [];
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });

  const [listName, setListName] = useState<string>(() => {
    if (isPublicList && publicUuid) {
      const cached = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      return cached || '';
    }
    if (isAuthenticated) {
      const cached = getCachedCurrentListName();
      if (cached) return cached;
    }
    const saved = sessionStorage.getItem('stocktable-list-name');
    return saved || 'Main List';
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [tickerBasicsIds, setTickerBasicsIds] = useState<Record<string, number>>({});
  const tickerBasicsIdsRef = useRef<Record<string, number>>({});
  const marketTypeBySymbolRef = useRef<Record<string, string>>({});
  useEffect(() => {
    tickerBasicsIdsRef.current = { ...tickerBasicsIds };
  }, [tickerBasicsIds]);

  const [tickerNames, setTickerNames] = useState<Record<string, string>>({});
  const [prosConsData, setProsConsData] = useState<Record<string, ProsConsRow>>({});

  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(new Set());
  const syncingTickersRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    syncingTickersRef.current = syncingTickers;
  }, [syncingTickers]);
  const unsyncedTickersRef = useRef<Set<string>>(new Set());
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const currentAttemptRef = useRef(1);

  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/proscons',
  });

  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'prosCons',
    persistToSessionStorageImmediately: false,
  });

  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.pros_cons_indicators_to_hide
    ? publicListData.listPreferences.pros_cons_indicators_to_hide
    : privateHiddenStatistics;

  // Fetch public list data
  useEffect(() => {
    const shouldFetch = publicUuid &&
      !publicListData &&
      !isLoadingPublicList &&
      publicListFetchAttemptedRef.current !== publicUuid;

    if (shouldFetch) {
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
        };
      }

      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);

      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });

          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);

          lastFetchedTickerListRef.current = '';
          setProsConsData({});
          setTickerBasicsIds({});

          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error: any) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }

    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
      setPublicListData(null);
      setPublicListError(null);

      if (!isAuthenticated && originalSessionStorageRef.current) {
        if (originalSessionStorageRef.current.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', originalSessionStorageRef.current.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (originalSessionStorageRef.current.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', originalSessionStorageRef.current.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        originalSessionStorageRef.current = null;
      }
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  const fetchTickerBasics = useCallback(async (symbols: string[]): Promise<Record<string, number>> => {
    if (symbols.length === 0) return {};

    const missingTickers = symbols.filter(t => !tickerBasicsIdsRef.current[t]);
    if (missingTickers.length === 0) {
      const cachedMap: Record<string, number> = {};
      symbols.forEach(t => {
        if (tickerBasicsIdsRef.current[t]) cachedMap[t] = tickerBasicsIdsRef.current[t];
      });
      return cachedMap;
    }

    if (isAuthenticated && currentListId && !isPublicList) {
      try {
        const listWithTickers = await userTickerListApi.getListWithTickers(currentListId);
        const basicsMap: Record<string, number> = {};
        listWithTickers.tickers.forEach((t: any) => {
          if (t.ticker_symbol && t.id) {
            basicsMap[t.ticker_symbol] = t.id;
            tickerBasicsIdsRef.current[t.ticker_symbol] = t.id;
          }
        });
        if (Object.keys(basicsMap).length > 0) {
          setTickerBasicsIds(prev => ({ ...prev, ...basicsMap }));
        }
        const allFound = missingTickers.every(t => basicsMap[t]);
        if (allFound && Object.keys(basicsMap).length > 0) {
          const out: Record<string, number> = {};
          symbols.forEach(s => {
            if (basicsMap[s] != null) out[s] = basicsMap[s];
          });
          return out;
        }
      } catch (err) {
        console.warn('ListProsCons: could not get ticker basics from list, falling back:', err);
      }
    }

    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerBasics(missingTickers);
      const map: Record<string, number> = {};
      if (response?.success && Array.isArray(response?.data)) {
        response.data.forEach((t: any) => {
          const symRaw = t?.ticker_symbol ?? t?.tickerSymbol;
          const idRaw = t?.ticker_basics_id ?? t?.tickerBasicsId ?? t?.id;
          if (!symRaw || !idRaw) return;
          const sym = String(symRaw);
          map[sym] = Number(idRaw);
          tickerBasicsIdsRef.current[sym] = Number(idRaw);
          const nameRaw = t?.ticker_name ?? t?.tickerName;
          if (nameRaw) setTickerNames(prev => ({ ...prev, [sym]: String(nameRaw) }));
          const mt = t?.market_type ?? t?.marketType;
          if (mt != null) marketTypeBySymbolRef.current[sym] = String(mt);
        });
      }
      setTickerBasicsIds(prev => ({ ...prev, ...map }));
      const out: Record<string, number> = {};
      symbols.forEach(sym => {
        const id = map[sym] ?? tickerBasicsIdsRef.current[sym];
        if (id != null) out[sym] = id;
      });
      return out;
    } catch (err) {
      console.error('ListProsCons: fetchTickerBasics error:', err);
    }
    const fallback: Record<string, number> = {};
    symbols.forEach(sym => {
      const id = tickerBasicsIdsRef.current[sym];
      if (id != null) fallback[sym] = id;
    });
    return fallback;
  }, [isAuthenticated, currentListId, isPublicList]);

  const loadMarketTypesForSymbols = useCallback(async (symbols: string[]) => {
    const need = symbols.filter(s => marketTypeBySymbolRef.current[s] === undefined);
    if (need.length === 0) return;
    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerBasics(need);
      if (response?.success && Array.isArray(response?.data)) {
        response.data.forEach((t: any) => {
          const symRaw = t?.ticker_symbol ?? t?.tickerSymbol;
          if (!symRaw) return;
          const sym = String(symRaw);
          const mt = t?.market_type ?? t?.marketType;
          marketTypeBySymbolRef.current[sym] = mt != null ? String(mt) : '';
        });
      }
    } catch (err) {
      console.warn('ListProsCons: could not load market types for ETF/ETV handling:', err);
    }
  }, [isAuthenticated]);

  const checkProsConsSyncAndFetchData = useCallback(async (tickersToCheck: string[], attemptNumber: number) => {
    if (pollingStoppedRef.current || isCheckingSyncRef.current) return;
    if (tickersToCheck.length === 0) return;
    isCheckingSyncRef.current = true;

    try {
      const basicsMap = await fetchTickerBasics(tickersToCheck);
      await loadMarketTypesForSymbols(tickersToCheck);

      const etfSymbols = tickersToCheck.filter(t => isEtfOrEtvMarketType(marketTypeBySymbolRef.current[t]));
      if (etfSymbols.length > 0) {
        unsyncedTickersRef.current = new Set(
          Array.from(unsyncedTickersRef.current).filter(s => !etfSymbols.includes(s)),
        );
        setSyncingTickers(prev => {
          const next = new Set(prev);
          etfSymbols.forEach(s => next.delete(s));
          return next;
        });
        setProsConsData(prev => {
          const next = { ...prev };
          etfSymbols.forEach(sym => {
            const id = basicsMap[sym] ?? tickerBasicsIdsRef.current[sym];
            if (id != null) {
              next[sym] = createEtfPlaceholderProsConsRow(sym, id);
            }
          });
          return next;
        });
      }

      const nonEtfTickers = tickersToCheck.filter(t => !etfSymbols.includes(t));
      const ids = nonEtfTickers
        .map(sym => basicsMap[sym] || tickerBasicsIdsRef.current[sym])
        .filter((id): id is number => typeof id === 'number');

      if (ids.length === 0) {
        if (nonEtfTickers.length === 0) {
          return;
        }
        // no ids yet for non-ETF, treat as syncing and poll
        setSyncingTickers(prev => new Set([...Array.from(prev), ...nonEtfTickers]));
        scheduleNextPoll(nonEtfTickers, attemptNumber);
        return;
      }

      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkProsAndConsSyncStatus({ ticker_basics_ids: ids });

      if (syncResponse?.success && syncResponse?.data?.tickers) {
        const idToSymbol: Record<number, string> = {};
        nonEtfTickers.forEach(sym => {
          const id = basicsMap[sym] || tickerBasicsIdsRef.current[sym];
          if (id) idToSymbol[id] = sym;
        });

        const syncedSymbols: string[] = [];
        const stillUnsyncedSymbols: string[] = [];
        const hasProsConsAccess = effectiveHasProRef.current;

        // Important requirement:
        // - no sync log => show '-' (NOT pulsing). So we do not add to syncingTickers.
        syncResponse.data.tickers.forEach((t: any) => {
          const sym = idToSymbol[t.ticker_basics_id];
          if (!sym) return;
          if (t.ticker_name) setTickerNames(prev => ({ ...prev, [sym]: t.ticker_name }));

          if (Boolean(t.is_synced)) {
            syncedSymbols.push(sym);
          } else {
            // Always keep polling for unsynced tickers (like other List* pages),
            // even if has_sync_log=false and is_syncing=false on early responses.
            stillUnsyncedSymbols.push(sym);
          }
        });

        // Track unsynced tickers in a ref so we can resume polling across re-renders.
        unsyncedTickersRef.current = new Set(stillUnsyncedSymbols);

        setSyncingTickers(prev => {
          const next = new Set(prev);
          syncedSymbols.forEach(s => next.delete(s));
          // Debugging + align with other List* pages: show pulses for any unsynced ticker,
          // regardless of viewer Pro status or sync-log presence.
          stillUnsyncedSymbols.forEach(s => next.add(s));
          return next;
        });

        // Fetch monthly pros/cons for synced ids
        if (syncedSymbols.length > 0) {
          const syncedIds = syncedSymbols
            .map(sym => basicsMap[sym] || tickerBasicsIdsRef.current[sym])
            .filter((id): id is number => typeof id === 'number');

          const dataResponse = await api.getLastProsAndCons({
            ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
            tickerBasicsIds: syncedIds,
          });

          if (dataResponse?.success && Array.isArray(dataResponse.data)) {
            const newData: Record<string, ProsConsRow> = {};
            dataResponse.data.forEach((row: any) => {
              newData[row.tickerSymbol] = row;
            });
            setProsConsData(prev => ({ ...prev, ...newData }));

            // Safety: once data is loaded, ensure tickers don't remain in syncing pulse state.
            // (Important for tab-switch-mid-sync scenarios.)
            const loadedSymbols = Object.keys(newData);
            if (loadedSymbols.length > 0) {
              unsyncedTickersRef.current = new Set(
                Array.from(unsyncedTickersRef.current).filter(s => !loadedSymbols.includes(s)),
              );
              setSyncingTickers(prev => {
                const next = new Set(prev);
                loadedSymbols.forEach((s: string) => next.delete(s));
                return next;
              });
            }

            // Update lastUpdated from sync ended times (best-effort)
            const ended = syncResponse.data.tickers
              .filter((t: any) => t.is_synced && t.last_sync_ended_at)
              .map((t: any) => new Date(t.last_sync_ended_at))
              .filter((d: Date) => !isNaN(d.getTime()));
            if (ended.length > 0) {
              setLastUpdated(new Date(Math.max(...ended.map((d: Date) => d.getTime()))));
            }
          }
        }

        // If nothing is left unsynced, stop polling immediately (clear interval).
        if (stillUnsyncedSymbols.length === 0) {
          unsyncedTickersRef.current = new Set();
          if (syncPollingIntervalRef.current) {
            clearInterval(syncPollingIntervalRef.current);
            syncPollingIntervalRef.current = null;
          }
          return;
        }

        if (stillUnsyncedSymbols.length > 0 && attemptNumber < 10) {
          scheduleNextPoll(stillUnsyncedSymbols, attemptNumber);
        }
      }
    } catch (err) {
      console.error('Error checking pros/cons sync status:', err);
    } finally {
      isCheckingSyncRef.current = false;
    }
  }, [fetchTickerBasics, loadMarketTypesForSymbols, isAuthenticated, isPublicList, publicListData]);

  const scheduleNextPoll = (tickers: string[], attemptNumber: number) => {
    if (pollingStoppedRef.current) return;
    const pollingDelay = 5000;
    if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
    currentAttemptRef.current = attemptNumber;
    syncPollingIntervalRef.current = setInterval(() => {
      if (pollingStoppedRef.current) {
        if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
        return;
      }
      // If a sync check is already in-flight, wait for the next interval tick.
      // (Do not burn attempt counts without making an API call.)
      if (isCheckingSyncRef.current) {
        return;
      }
      const nextAttempt = currentAttemptRef.current + 1;
      if (nextAttempt > 10) {
        if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
        pollingStoppedRef.current = true;
        // Stop showing pulses once we give up polling.
        setSyncingTickers(prev => {
          const next = new Set(prev);
          tickers.forEach(t => next.delete(t));
          return next;
        });
        return;
      }
      currentAttemptRef.current = nextAttempt;
      checkProsConsSyncAndFetchData([...tickers], nextAttempt);
    }, pollingDelay);
  };

  // Initial load + refresh when list changes
  useEffect(() => {
    if (isPublicList && !publicListData) {
      return;
    }

    if (!tickerList || tickerList.length === 0) {
      lastFetchedTickerListRef.current = '';
      prevTickerListRef.current = [];
      return;
    }

    const ownerProStatus = isPublicList && publicListData ? String(publicListData.ownerHasProSubscription) : '';
    const tickerListKey = [...tickerList].sort().join(',') + (ownerProStatus ? `|pro:${ownerProStatus}` : '');
    const hasActiveSyncPolling = syncingTickersRef.current.size > 0 || unsyncedTickersRef.current.size > 0;
    if (lastFetchedTickerListRef.current === tickerListKey && !hasActiveSyncPolling) {
      return;
    }
    lastFetchedTickerListRef.current = tickerListKey;

    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    setLoading(true);
    setError(null);

    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }

    const prev = prevTickerListRef.current || [];
    const isFirstLoad = prev.length === 0;
    const addedTickers = tickerList.filter(t => !prev.includes(t));
    prevTickerListRef.current = [...tickerList];

    const tickersToCheck = isFirstLoad
      ? [...tickerList]
      : (addedTickers.length > 0
        ? addedTickers
        : Array.from(unsyncedTickersRef.current.size > 0 ? unsyncedTickersRef.current : syncingTickersRef.current));
    if (tickersToCheck.length === 0) {
      setLoading(false);
      return;
    }

    checkProsConsSyncAndFetchData(tickersToCheck, 1)
      .finally(() => setLoading(false));

    return () => {
      if (syncPollingIntervalRef.current) {
        clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
      }
    };
  }, [tickerList, checkProsConsSyncAndFetchData, isPublicList, publicListData]);

  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null
      ? `stocktable-ticker-list-${currentListId}`
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  const autoSaveList = useCallback(async (tickers: string[]) => {
    if (isPublicList || !isAuthenticated) return;

    let listId = currentListId;
    if (listId === null) {
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        listId = cachedListId;
      } else if (userLists.length > 0) {
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const exists = userLists.some(l => l.id === defaultListResponse.defaultUserTickerListId);
            listId = exists ? defaultListResponse.defaultUserTickerListId : userLists[0].id;
          } else {
            listId = userLists[0].id;
          }
        } catch {
          listId = userLists[0].id;
        }
      } else {
        return;
      }
    }

    if (!listId) return;

    try {
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1,
      }));
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      clearListCache(listId);
    } catch (e) {
      console.error('ListProsCons autoSaveList failed:', e);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    if (isPublicList) return;

    const newTickerList = [...saveTempTickerList];

    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    await savePreferences(hiddenStatistics);

    if (isAuthenticated && currentListId) {
      try {
        if (saveTempListName !== listName) {
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          setUserLists((prev: UserTickerList[]) =>
            prev.map((list: UserTickerList) =>
              list.id === currentListId ? { ...list, list_name: saveTempListName } : list
            )
          );
          cacheCurrentListName(saveTempListName);
        }
        await autoSaveList(newTickerList);
        try {
          await Promise.all([usageLimitsApi.checkTickerLimit(), usageLimitsApi.checkListLimit()]);
        } catch (e) {
          console.error('ListProsCons: refresh usage limits failed:', e);
        }
      } catch (e) {
        console.error('ListProsCons: failed to update list:', e);
      }
    } else {
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
    }
  }, [
    isPublicList,
    isAuthenticated,
    currentListId,
    listName,
    hiddenStatistics,
    getTickerListKey,
    setUserLists,
    autoSaveList,
    savePreferences,
  ]);

  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/proscons',
    onSaveChanges: handleSaveChanges,
  });

  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
  });

  const handleRemoveTicker = useCallback(async (tickerToRemove: string) => {
    if (isEditingTickers) {
      const newTempList = tempTickerList.filter(t => t !== tickerToRemove);
      setTempTickerList(newTempList);
    } else {
      const newList = tickerList.filter(t => t !== tickerToRemove);
      setTickerList(newList);
      setProsConsData(prev => {
        const next = { ...prev };
        delete next[tickerToRemove];
        return next;
      });
      if (isAuthenticated) {
        await autoSaveList(newList);
      }
    }
  }, [isEditingTickers, tempTickerList, setTempTickerList, tickerList, setTickerList, isAuthenticated, autoSaveList]);

  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>>,
    isEditingTickers,
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string) => {
      if (tickerName) setTickerNames(prev => ({ ...prev, [ticker]: tickerName }));
    }, []),
    onSyncComplete: useCallback(
      async (ticker: string) => {
        await checkProsConsSyncAndFetchData([ticker], 1);
      },
      [checkProsConsSyncAndFetchData]
    ),
    autoSaveList,
    saveListImmediately: useCallback(
      async (tickers: string[]) => {
        const uniqueTickers = Array.from(new Set(tickers));
        if (isPublicList) return;

        if (isAuthenticated && !isPublicList) {
          let listId = currentListId;
          if (listId === null) {
            const cachedListId = getCachedCurrentListId();
            if (cachedListId) listId = cachedListId;
            else if (userLists.length > 0) {
              try {
                const defaultListResponse = await userApi.getDefaultList();
                if (
                  defaultListResponse.defaultUserTickerListId &&
                  userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)
                ) {
                  listId = defaultListResponse.defaultUserTickerListId;
                } else {
                  listId = userLists[0].id;
                }
              } catch {
                listId = userLists[0].id;
              }
            }
          }
          if (listId != null) {
            try {
              const tickerMappings = uniqueTickers.map((ticker, index) => ({
                ticker_symbol: ticker,
                order_num: index + 1,
              }));
              await userTickerListApi.updateTickerMappings(listId, tickerMappings);
              clearListCache(listId);
            } catch (e) {
              console.error('ListProsCons saveListImmediately failed:', e);
            }
          }
          return;
        }

        sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickers));
        if (currentListId !== null) {
          sessionStorage.setItem(`stocktable-ticker-list-${currentListId}`, JSON.stringify(uniqueTickers));
        }
      },
      [currentListId, isPublicList, isAuthenticated, userLists]
    ),
    pollingInterval: 2000,
    maxPollAttempts: 10,
    normalizeTicker: (ticker: string) => ticker.toUpperCase(),
  });

  const sortedUserLists = useMemo(() => {
    if (!userLists) return [];
    return [...userLists].sort((a, b) => (a.list_name || '').localeCompare(b.list_name || ''));
  }, [userLists]);

  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  const isCurrentListLocked = useMemo(
    () => currentListId !== null && lockedListIds.includes(currentListId),
    [currentListId, lockedListIds]
  );

  const currentList =
    currentListId && userLists ? userLists.find(list => list.id === currentListId) || null : null;

  const handleShareList = useCallback(() => setIsShareModalOpen(true), []);
  const handleShareModalClose = useCallback(() => setIsShareModalOpen(false), []);
  const handleShareModalUpdate = useCallback(async () => {
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
      } catch (e) {
        console.error('ListProsCons: refresh lists after share failed:', e);
      }
    }
  }, [isAuthenticated, currentListId, setUserLists]);

  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setTickerList([]);
      setListName('Main List');
      setHiddenStatistics([]);
      setProsConsData({});
    },
  });

  const displayTickerList = useMemo(
    () => (isEditingTickers ? tempTickerList : tickerList),
    [isEditingTickers, tempTickerList, tickerList]
  );

  const tickerData: TickerDisplayData[] = useMemo(() => {
    return displayTickerList.map(symbol => {
      const row = prosConsData[symbol];
      const base: any = {
        ticker: symbol,
        name: tickerNames[symbol] || symbol,
        ticker_symbol: symbol,
        ticker_name: tickerNames[symbol] || '',
        ticker_basics_id: tickerBasicsIdsRef.current[symbol] || row?.tickerBasicsId,
        isSyncing: syncingTickers.has(symbol),
      };

      const pc = row?.prosCons || null;
      const { pros, cons } = prosConsLineArrays(pc);
      (base as any).pros = pros;
      (base as any).cons = cons;

      return base as TickerDisplayData;
    });
  }, [displayTickerList, prosConsData, tickerNames, syncingTickers]);

  const getTickerData = useCallback((symbol: string) => tickerData.find(t => t.ticker === symbol), [tickerData]);

  const formatStatisticValue = useCallback((statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    const value = (ticker as any)[statisticKey];

    // Always show pulsing placeholders while syncing.
    if ((ticker as any).isSyncing) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" title="Syncing..." />;
    }

    if (lockedStatistics.includes(statisticKey)) {
      const emptyProsCons =
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);
      if (emptyProsCons) {
        return (
          <LockIcon
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
            onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined}
            tooltipText={isPublicList ? "Locked due to the list owner's plan" : 'Upgrade to the Pro Plan to see'}
          />
        );
      }
    }

    if (statisticKey === 'pros' || statisticKey === 'cons') {
      if (Array.isArray(value) && value.length > 0) {
        return <ProsConsNumberedList lines={value} />;
      }
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        return <span className="text-gray-400 dark:text-text-secondary">-</span>;
      }
    }

    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }

    const text = String(value);
    return (
      <div className="text-xs leading-tight whitespace-pre-wrap break-words">
        {text}
      </div>
    );
  }, [lockedStatistics, isPublicList, navigate]);

  const getStatisticColor = useCallback((_statisticKey: string, ticker: TickerDisplayData): string => {
    if ((ticker as any).isSyncing) return 'text-text-light-neutral dark:text-text-secondary';
    return 'text-text-light-neutral dark:text-text-secondary';
  }, []);

  const handleTickerClick = useCallback(
    (tickerSymbol: string) => {
      const id = tickerBasicsIdsRef.current[tickerSymbol];
      const state = id != null ? { tickerBasicsId: id } : undefined;
      if (publicUuid) {
        navigate(`/ticker/${tickerSymbol}/proscons?public_uuid=${publicUuid}`, { state });
      } else {
        navigate(`/ticker/${tickerSymbol}/proscons`, { state });
      }
    },
    [navigate, publicUuid]
  );

  if (publicUuid && isPublicListNotFound) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        {!isAuthenticated && <PublicListPromoBar />}
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-gray-700 dark:text-text-primary">
              {publicListError || 'This list does not exist or is no longer available.'}
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <StockTableLayout
        tickerList={displayTickerList}
        tickerData={tickerData}
        loading={loading || isLoadingPublicList || isLoadingUserLists}
        error={error || (isPublicList ? publicListError : null)}
        lastUpdated={lastUpdated}
        isEditingTickers={isPublicList ? false : isEditingTickers}
        isPublicList={isPublicList}
        showPublicListPromoBar={isPublicList && !isAuthenticated}
        hiddenStatistics={hiddenStatistics}
        draggedTicker={draggedTicker}
        dragOverTicker={dragOverTicker}
        droppedTicker={droppedTicker}
        statistics={PROS_CONS_STATISTICS}
        categoryHeaders={[]}
        getTickerData={getTickerData}
        formatStatisticValue={formatStatisticValue}
        getStatisticColor={getStatisticColor}
        lockedStatistics={lockedStatistics}
        proOnlyStatistics={PROS_CONS_STATISTICS.map(s => s.key)}
        statisticsColumnWidthMultiplier={1.15}
        fullScreenStatisticsColumnWidthMultiplier={1.15}
        useListDailySmallScreenColumnWidth
        tickerColumnWidth="216px"
        onRefetch={() => checkProsConsSyncAndFetchData([...tickerList], 1)}
        onTickerClick={handleTickerClick}
        onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
        onDragStart={isPublicList ? () => {} : handleDragStart}
        onDragOver={isPublicList ? () => {} : handleDragOver}
        onDragLeave={isPublicList ? () => {} : handleDragLeave}
        onDrop={isPublicList ? () => {} : handleDrop}
        onDragEnd={isPublicList ? () => {} : handleDragEnd}
        onHideStatistic={handleHideStatistic}
        onShowStatistic={handleShowStatistic}
        listName={listName}
        onStartEdit={isPublicList ? () => {} : handleStartEdit}
        onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
        onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
        onAddTicker={handleAddTicker}
        onSave={isPublicList ? () => {} : handleSave}
        tempListName={tempListName}
        onTempListNameChange={setTempListName}
        userLists={isPublicList ? [] : userLists}
        currentListId={isPublicList ? undefined : currentListId || undefined}
        onListChange={isPublicList ? undefined : handleListChange}
        onCreateNewList={isPublicList ? undefined : handleCreateNewList}
        onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
        onDeleteList={handleDeleteList}
        showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
        onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
        onRefreshUsageLimits={() => {}}
        tabs={<TabBar activeTab="proscons" disabled={false} publicUuid={publicUuid} />}
        showRefreshButton={false}
        showDelayedPricesText={false}
        showLastUpdatedDate={true}
        prosConsComfortablePadding
      />
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>

      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
        isListLocked={isCurrentListLocked}
      />

      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />

      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default ListProsCons;

